// Duel 1v1 — rotation auto-battle engine (Ulala-style, PvP-symmetric).
//
// Replaces the 24-phase football simulation when DUEL_ROTATION_ENABLED is on:
// two SOLO fighters (no teams), each with an HP pool derived from equipment
// stats and a 4-spell ROTATION. Turns alternate attacker → defender; each turn
// the fighter auto-attacks (+ROTATION_ENERGY_PER_AUTO gauge points, banked),
// then fires its rotation HEAD if affordable — strict order, never skipped.
//
// The ATTACKER runs its saved ATTACK rotation; the DEFENDER (ghost — a real
// player offline, or a bot) runs its saved DEFENSE rotation. Players with no
// loadout get DEFAULT_*_ROTATION (beta fill).
//
// Pure state machine, ZERO persistence, ZERO React — mirrors the PVE
// interactive engine's contract. Reuses the PVE spell catalogue and the
// rotation calibration constants so both modes stay in sync.
//
// Spell-effect deviations from PVE (1 action per turn makes some effects dead):
//   - Focus (turnDamageBonusPercent): persists until the fighter's NEXT
//     offensive spell instead of "rest of this turn" (which would be never).
//   - oncePerTurn: ignored (one action per turn by construction).
//   - Vision echo: marks the OPPONENT — at the end of their next turn they
//     take echo damage per gauge energy they spent that turn.

import type { EquipmentStats } from "./combat";
import { getSpell, isValidSpell } from "../../stadium-trial/content/pveSpells";
import type { PveSpellId } from "../../stadium-trial/content/pveSpells";
import {
  ROTATION_GAUGE_SCALE,
  ROTATION_ENERGY_MAX,
  ROTATION_ENERGY_PER_AUTO,
  ROTATION_AUTO_DAMAGE_PERCENT,
  ROTATION_SPELL_DAMAGE_MULT,
  rotationSpellCost,
} from "../../stadium-trial/engine/pveCombatInteractive";
import { computeHit, maxHpFromStats } from "../../stadium-trial/engine/pveEngine";

// ── Flags & defaults ──────────────────────────────────────────────────────────

/** Master switch — swaps the football match for the rotation auto-battle. */
export const DUEL_ROTATION_ENABLED = true;

export const DUEL_ROTATION_SIZE = 4;
/** A rotation needs at least 2 spells — a 2-spell loop is a legitimate build
 *  (cheap spells cycling fast), not an error state. */
export const MIN_DUEL_ROTATION = 2;
/** Rotation slots unlock with the player level: 2 at start, 3rd at Lv 10,
 *  4th at Lv 20. */
export const ROTATION_SLOT_LEVELS = [1, 1, 10, 20] as const;
export function duelRotationSlots(level: number): number {
  return ROTATION_SLOT_LEVELS.filter((lv) => level >= lv).length;
}
/** Crit damping — DUEL ONLY. computeHit bakes a ×2 crit that, stacked on the
 *  ×3 rotation spell mult (+ Focus/Feint buffs), produced ~9× swings (a normal
 *  turn ≈ -30, then a single crit spell ≈ -275). In PvP that feels like a coin
 *  flip decides the match. We soften the crit BONUS here (not in computeHit, so
 *  the PVE stays untouched): effective crit ≈ ×1.5 instead of ×2. */
export const DUEL_CRIT_DAMP = 0.75; // crit ×2 × 0.75 = ×1.5 → crits deal +50%.

/** Applies the duel crit damping to a hit whose damage already includes the
 *  raw ×2 crit from computeHit. No-op for non-crits. Result: crits = +50%. */
function dampCrit(damage: number, crit: boolean): number {
  return crit ? damage * DUEL_CRIT_DAMP : damage;
}

/** Spell damage amplifier — DUEL ONLY. Stacks on ROTATION_SPELL_DAMAGE_MULT so
 *  every spell (and the Vision echo) hits +100% harder in the duel without
 *  touching the shared PVE calibration. Spells are the payoff of the rotation;
 *  this widens the gap over the weak autos. */
export const DUEL_SPELL_DAMAGE_MULT = 2;

/** Total damage factor a spell's `damagePercent` gets in a duel: the shared
 *  rotation mult (×3) stacked with the duel-only amp (×2) = ×6. Exposed so the
 *  spell-setup UI can preview real duel numbers. */
export const DUEL_SPELL_DAMAGE_FACTOR = ROTATION_SPELL_DAMAGE_MULT * DUEL_SPELL_DAMAGE_MULT;

/** Estimated duel damage of a spell for a given Puissance (physique): an average
 *  hit (no ±roll), non-crit, before enemy defense — the number shown in the
 *  rotation setup so the player can project their attack. Returns 0 for spells
 *  with no direct damage (Focus, Wall). */
export function estimateDuelSpellDamage(physique: number, id: PveSpellId): number {
  const pct = getSpell(id)?.effects.damagePercent;
  if (!pct) return 0;
  return Math.round((physique * pct / 100) * DUEL_SPELL_DAMAGE_FACTOR);
}

/** Both fighters act once per round; past the cap the fight resolves by HP
 *  fraction (exact tie → the DEFENDER holds, like the old "draw = loss"). */
export const MAX_DUEL_ROUNDS = 30;

/** Beta fill when a player has no attack loadout equipped. Tuned by simulation
 *  so the two defaults are near-even at equal stats (attacker first-move ≈
 *  compensates the defensive Wall). */
export const DEFAULT_ATTACK_ROTATION: PveSpellId[] = ["classic_strike", "wall", "feint", "nutmeg"];
/** Beta fill when a defender has no defense loadout equipped. */
export const DEFAULT_DEFENSE_ROTATION: PveSpellId[] = ["classic_strike", "wall", "nutmeg", "feint"];

/** Sanitize a stored rotation: known ids only, capped at DUEL_ROTATION_SIZE.
 *  A saved rotation of MIN_DUEL_ROTATION+ spells is respected as-is (a short
 *  loop is a build choice). Below the minimum, it is padded with fallback
 *  spells (no duplicates) up to `slots` — the level-gated slot count. */
export function normalizeRotation(ids: unknown, fallback: PveSpellId[], slots: number = DUEL_ROTATION_SIZE): PveSpellId[] {
  const cap = Math.max(MIN_DUEL_ROTATION, Math.min(DUEL_ROTATION_SIZE, slots));
  const clean = Array.isArray(ids)
    ? (ids.filter((x) => typeof x === "string" && isValidSpell(x)) as PveSpellId[]).slice(0, DUEL_ROTATION_SIZE)
    : [];
  if (clean.length >= MIN_DUEL_ROTATION) return clean;
  for (const id of fallback) {
    if (clean.length >= cap) break;
    if (!clean.includes(id)) clean.push(id);
  }
  return clean;
}

// ── State ─────────────────────────────────────────────────────────────────────

export type DuelSide = "attacker" | "defender";

export interface DuelFighter {
  name: string;
  stats: EquipmentStats;
  hp: number;
  maxHp: number;
  shield: number;
  energy: number;               // gauge points (0..ROTATION_ENERGY_MAX), banked
  rotation: PveSpellId[];
  rotationIndex: number;        // next slot to fire (wraps)
  cooldowns: Partial<Record<PveSpellId, number>>; // remaining OWN turns
  /** Focus — +X% on this fighter's NEXT offensive spell (consumed). */
  focusBonusPercent: number;
  /** Feint — +X% FINAL damage on this fighter's next offensive spell (consumed). */
  offBalancePercent: number;
  /** Vision mark ON this fighter (set by the opponent): echo % per gauge
   *  energy this fighter spends on its next turn. */
  echoIncomingPerEnergy: number;
  /** Wall bookkeeping — expiry at the start of this fighter's Nth own turn. */
  shieldExpiresTurn: number | null;
  wallCritGain: number;
  /** This fighter's own turn counter (cooldowns/wall timing). */
  ownTurn: number;
  // Telemetry
  autoAttacks: number;
  spellsCast: Partial<Record<PveSpellId, number>>;
  energySpent: number;
}

export interface DuelCombatEvent {
  id: number;
  side: DuelSide;               // who acted
  kind: "auto" | "spell" | "echo";
  spellId?: PveSpellId;
  damage: number;
  crit: boolean;
  heal?: number;
  selfDamage?: number;
  shieldGained?: number;
  /** HP snapshots AFTER the event (both sides — drives the UI bars). */
  attackerHp: number;
  defenderHp: number;
  /** Gauge snapshots AFTER the event (both sides — drives the energy bars so
   *  the gauge visibly fills on the auto hit and drains on the spell, exactly
   *  like the Stadium rotation. Without these the bar jumps to the post-turn
   *  value and the fill-to-max is never seen). */
  attackerEnergy: number;
  defenderEnergy: number;
  /** Rotation HEAD snapshot AT this event — the spell each fighter is charging
   *  toward. On the AUTO event the acting head is still the spell about to fire,
   *  so its cost is the gauge CHECKPOINT and the charged gauge hits it (green
   *  "reached" flash, like the Stadium). Reading the live head instead would
   *  already show the NEXT spell (rotationIndex advanced) and the flash is lost. */
  attackerHead: PveSpellId | null;
  defenderHead: PveSpellId | null;
}

export interface DuelRotationState {
  round: number;                // 1-based; a round = attacker turn + defender turn
  active: DuelSide;             // whose turn plays next
  attacker: DuelFighter;
  defender: DuelFighter;
  over: boolean;
  winner: DuelSide | null;
  events: DuelCombatEvent[];
}

let EVENT_SEQ = 0;

// ── Creation ──────────────────────────────────────────────────────────────────

function makeFighter(name: string, stats: EquipmentStats, rotation: PveSpellId[]): DuelFighter {
  const maxHp = Math.max(1, maxHpFromStats(stats.vitesse));
  return {
    name,
    stats,
    hp: maxHp,
    maxHp,
    shield: 0,
    energy: 0,
    rotation: [...rotation],
    rotationIndex: 0,
    cooldowns: {},
    focusBonusPercent: 0,
    offBalancePercent: 0,
    echoIncomingPerEnergy: 0,
    shieldExpiresTurn: null,
    wallCritGain: 0,
    ownTurn: 0,
    autoAttacks: 0,
    spellsCast: {},
    energySpent: 0,
  };
}

export function createDuelRotationCombat(
  attackerName: string,
  attackerStats: EquipmentStats,
  attackerRotation: string[] | undefined,
  defenderName: string,
  defenderStats: EquipmentStats,
  defenderRotation: string[] | undefined,
  /** Level-gated slot count for FALLBACK rotations (attacker's level, used as
   *  proxy for the defender too — MMR-matched opponents ≈ same level). */
  fallbackSlots: number = DUEL_ROTATION_SIZE
): DuelRotationState {
  return {
    round: 1,
    active: "attacker",
    attacker: makeFighter(attackerName, attackerStats, normalizeRotation(attackerRotation, DEFAULT_ATTACK_ROTATION, fallbackSlots)),
    defender: makeFighter(defenderName, defenderStats, normalizeRotation(defenderRotation, DEFAULT_DEFENSE_ROTATION, fallbackSlots)),
    over: false,
    winner: null,
    events: [],
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const other = (s: DuelSide): DuelSide => (s === "attacker" ? "defender" : "attacker");
const fighterOf = (st: DuelRotationState, s: DuelSide): DuelFighter => (s === "attacker" ? st.attacker : st.defender);

export function duelRotationHead(f: DuelFighter): PveSpellId | null {
  return f.rotation.length > 0 ? f.rotation[f.rotationIndex % f.rotation.length] : null;
}

export function canCastDuelSpell(f: DuelFighter, id: PveSpellId): boolean {
  const def = getSpell(id);
  if (!def || f.energy < def.cost * ROTATION_GAUGE_SCALE) return false;
  if ((f.cooldowns[id] ?? 0) > 0) return false;
  const hpCost = def.effects.selfDamagePercentMaxHp;
  if (hpCost != null && f.hp <= (hpCost / 100) * f.maxHp) return false;
  return true;
}

/** Damage lands on the shield first; enemy crits feed an active wall. */
function applyDamage(target: DuelFighter, damage: number, crit: boolean): void {
  const absorbed = Math.min(target.shield, damage);
  target.shield -= absorbed;
  target.hp = Math.max(0, target.hp - (damage - absorbed));
  if (crit && target.shieldExpiresTurn != null && target.wallCritGain > 0) {
    target.shield += target.wallCritGain;
  }
}

function cloneFighter(f: DuelFighter): DuelFighter {
  return { ...f, rotation: f.rotation, cooldowns: { ...f.cooldowns }, spellsCast: { ...f.spellsCast } };
}

function cloneState(st: DuelRotationState): DuelRotationState {
  return { ...st, attacker: cloneFighter(st.attacker), defender: cloneFighter(st.defender), events: [...st.events] };
}

function pushEvent(
  st: DuelRotationState,
  ev: Omit<DuelCombatEvent, "id" | "attackerHp" | "defenderHp" | "attackerEnergy" | "defenderEnergy" | "attackerHead" | "defenderHead">
): void {
  st.events.push({
    ...ev,
    id: ++EVENT_SEQ,
    attackerHp: Math.round(st.attacker.hp),
    defenderHp: Math.round(st.defender.hp),
    attackerEnergy: st.attacker.energy,
    defenderEnergy: st.defender.energy,
    attackerHead: duelRotationHead(st.attacker),
    defenderHead: duelRotationHead(st.defender),
  });
}

function checkOver(st: DuelRotationState): void {
  if (st.attacker.hp <= 0) { st.over = true; st.winner = "defender"; }
  else if (st.defender.hp <= 0) { st.over = true; st.winner = "attacker"; }
}

// ── Turn resolution ───────────────────────────────────────────────────────────

/**
 * Plays the ACTIVE fighter's whole turn (auto attack → rotation head if
 * affordable → incoming Vision echo), then hands the turn to the other side.
 * Returns a NEW immutable state; call repeatedly until `over`.
 */
export function stepDuelRotation(prev: DuelRotationState, rng: () => number = Math.random): DuelRotationState {
  if (prev.over) return prev;
  const st = cloneState(prev);
  const side = st.active;
  const self = fighterOf(st, side);
  const opp = fighterOf(st, other(side));

  // ── Own-turn upkeep: turn counter, cooldown tick, wall expiry ──
  self.ownTurn += 1;
  for (const key of Object.keys(self.cooldowns) as PveSpellId[]) {
    const left = (self.cooldowns[key] ?? 0) - 1;
    if (left <= 0) delete self.cooldowns[key];
    else self.cooldowns[key] = left;
  }
  if (self.shieldExpiresTurn != null && self.ownTurn >= self.shieldExpiresTurn) {
    self.shield = 0;
    self.shieldExpiresTurn = null;
    self.wallCritGain = 0;
  }

  let energySpentThisTurn = 0;

  // ── 1. Auto attack — weak hit, +gauge income (banked) ──
  const autoHit = computeHit(self.stats, opp.stats, 1, rng);
  const autoDamage = dampCrit(autoHit.damage, autoHit.crit) * (ROTATION_AUTO_DAMAGE_PERCENT / 100);
  applyDamage(opp, autoDamage, autoHit.crit);
  self.autoAttacks += 1;
  self.energy = Math.min(ROTATION_ENERGY_MAX, self.energy + ROTATION_ENERGY_PER_AUTO);
  pushEvent(st, { side, kind: "auto", damage: Math.round(autoDamage), crit: autoHit.crit });
  checkOver(st);

  // ── 2. Rotation head — strict order, cast if affordable ──
  const head = duelRotationHead(self);
  if (!st.over && head && canCastDuelSpell(self, head)) {
    const def = getSpell(head)!;
    const e = def.effects;
    const cost = def.cost * ROTATION_GAUGE_SCALE;
    self.energy -= cost;
    self.energySpent += cost;
    energySpentThisTurn += cost;
    self.spellsCast[head] = (self.spellsCast[head] ?? 0) + 1;
    self.rotationIndex += 1;
    if (e.cooldownTurns != null) self.cooldowns[head] = e.cooldownTurns;

    let damage = 0;
    let crit = false;
    let healed = 0;
    let selfDamage = 0;
    let shieldGained = 0;

    // Focus — arm the next-offensive-spell buff (duel deviation, see header).
    if (e.turnDamageBonusPercent != null) {
      self.focusBonusPercent += e.turnDamageBonusPercent;
    }

    // Reckless Strike — HP cost up front (canCastDuelSpell guarantees survival).
    if (e.selfDamagePercentMaxHp != null) {
      selfDamage = (e.selfDamagePercentMaxHp / 100) * self.maxHp;
      self.hp = Math.max(0, self.hp - selfDamage);
    }

    if (e.damagePercent != null) {
      const hit = computeHit(self.stats, opp.stats, 1, rng);
      crit = hit.crit;
      let mult = (e.damagePercent / 100) * ROTATION_SPELL_DAMAGE_MULT * DUEL_SPELL_DAMAGE_MULT;
      if (self.focusBonusPercent > 0) {
        mult *= 1 + self.focusBonusPercent / 100;
        self.focusBonusPercent = 0; // consumed by the next offensive spell
      }
      damage = dampCrit(hit.damage, crit) * mult;
      if (self.offBalancePercent > 0) {
        damage *= 1 + self.offBalancePercent / 100;
        self.offBalancePercent = 0;
      }
      applyDamage(opp, damage, crit);

      if (crit) {
        if (e.critEnergyRefund != null) {
          self.energy = Math.min(ROTATION_ENERGY_MAX, self.energy + e.critEnergyRefund * ROTATION_GAUGE_SCALE);
        }
        if (e.healOnCritPercentMaxHp != null) {
          healed = (e.healOnCritPercentMaxHp / 100) * self.maxHp;
          self.hp = Math.min(self.maxHp, self.hp + healed);
        }
      }
    }

    // Feint — arm Off Balance for the NEXT offensive spell.
    if (e.applyOffBalancePercent != null) {
      self.offBalancePercent = e.applyOffBalancePercent;
    }

    // Vision — mark the opponent: echo per gauge energy they spend next turn.
    if (e.echoPerEnemyEnergyPercent != null) {
      opp.echoIncomingPerEnergy = e.echoPerEnemyEnergyPercent;
    }

    // Wall — timed flat shield, grows on enemy crits received. Recast replaces.
    if (e.shieldFlat != null) {
      self.shield = e.shieldFlat;
      shieldGained = e.shieldFlat;
      self.shieldExpiresTurn = self.ownTurn + (e.shieldDurationTurns ?? 2);
      self.wallCritGain = e.shieldPerEnemyCrit ?? 0;
    }

    pushEvent(st, {
      side, kind: "spell", spellId: head,
      damage: Math.round(damage), crit,
      heal: healed > 0 ? Math.round(healed) : undefined,
      selfDamage: selfDamage > 0 ? Math.round(selfDamage) : undefined,
      shieldGained: shieldGained > 0 ? shieldGained : undefined,
    });
    checkOver(st);
  }

  // ── 3. Incoming Vision echo — punishes the gauge energy spent THIS turn.
  // Scaled per catalogue unit (÷ROTATION_GAUGE_SCALE) to match PVE numbers.
  if (!st.over && self.echoIncomingPerEnergy > 0) {
    if (energySpentThisTurn > 0) {
      const hit = computeHit(opp.stats, self.stats, 1, rng);
      const units = energySpentThisTurn / ROTATION_GAUGE_SCALE;
      const echo = hit.damage * (self.echoIncomingPerEnergy / 100) * units * ROTATION_SPELL_DAMAGE_MULT * DUEL_SPELL_DAMAGE_MULT;
      applyDamage(self, echo, false);
      pushEvent(st, { side: other(side), kind: "echo", spellId: "vision", damage: Math.round(echo), crit: hit.crit });
    }
    self.echoIncomingPerEnergy = 0;
    checkOver(st);
  }

  if (st.over) return st;

  // ── Hand over / round rollover / timeout ──
  if (side === "defender") {
    if (st.round >= MAX_DUEL_ROUNDS) {
      st.over = true;
      const atkFrac = st.attacker.hp / st.attacker.maxHp;
      const defFrac = st.defender.hp / st.defender.maxHp;
      st.winner = atkFrac > defFrac ? "attacker" : "defender"; // tie → defense holds
      return st;
    }
    st.round += 1;
  }
  st.active = other(side);
  return st;
}

/** Telemetry payload for duel_history / logs (extend freely). */
export function duelRotationStats(st: DuelRotationState): Record<string, unknown> {
  const pack = (f: DuelFighter) => ({
    rotation: f.rotation,
    auto_attacks: f.autoAttacks,
    spells_cast: f.spellsCast,
    energy_spent: f.energySpent,
    hp_end: Math.round(f.hp),
    max_hp: f.maxHp,
  });
  return { rounds: st.round, winner: st.winner, attacker: pack(st.attacker), defender: pack(st.defender) };
}
